import type { DeliveryOrderBody } from '../apis/logistics.js';
import {
  SupplierNameTakenError,
  SupplierPhoneInvalidError,
  type ISupplierBody,
  type ISupplierPatch,
} from '../apis/suppliers.js';
import type { Supplier } from '../interfaces/ILogistics.js';

/** The supplier's contact person and phone as one line — whichever exist, or
 *  null when neither does (no stray separator, no empty link). */
export function supplierContactLine(s: Supplier): string | null {
  const parts = [s.contactName, s.phone].filter((p): p is string => p != null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The supplier an order points at, or null for no id or one the list no
 *  longer holds (a stale cache must read as "no supplier", never crash). */
export function supplierById(list: Supplier[], id: string | null): Supplier | null {
  return id == null ? null : (list.find((s) => s.id === id) ?? null);
}

/** The supplier fields as the mini-form holds them — plain strings, as typed. */
export interface ISupplierFields {
  name: string;
  contactName: string;
  phone: string;
}

/** A supplier typed into the order form, saved WITH the order (spec
 *  2026-10-25 §12): a new one, or changes to `of`, the saved supplier the
 *  order points at. */
export type ISupplierDraft = ISupplierFields & ({ mode: 'new' } | { mode: 'edit'; of: Supplier });

/** The order form's supplier choice: the picked id (null for none) and the
 *  open draft, if any. */
export interface IOrderSupplierChoice {
  id: string | null;
  draft: ISupplierDraft | null;
}

/** A new-supplier draft, starting from what the user was already typing. */
export function newSupplierDraft(name: string): ISupplierDraft {
  return { mode: 'new', name, contactName: '', phone: '' };
}

/** An edit draft of a saved supplier, prefilled with its values. */
export function editSupplierDraft(of: Supplier): ISupplierDraft {
  return { mode: 'edit', of, name: of.name, contactName: of.contactName ?? '', phone: of.phone ?? '' };
}

/** Save waits while a draft has no Company: the server would refuse it, and
 *  the hint says how to go on (type one, or cancel the draft). */
export function draftBlocksSave(draft: ISupplierDraft | null): boolean {
  return draft !== null && draft.name.trim() === '';
}

/** What a refused order write means for its supplier fields: a 409 name clash
 *  or a 400 phone. Null for every other refusal — the order's own error
 *  feedback (toast, alert, dialog line) reports those. */
export type SupplierSaveError = 'taken' | 'phone';

export function supplierSaveErrorOf(error: unknown): SupplierSaveError | null {
  if (error instanceof SupplierNameTakenError) return 'taken';
  return error instanceof SupplierPhoneInvalidError ? 'phone' : null;
}

/** The mini-form's typed fields as a write body: trimmed, blanks as null. */
export function supplierBodyOf(fields: ISupplierFields): ISupplierBody {
  return {
    name: fields.name.trim(),
    contactName: fields.contactName.trim() || null,
    phone: fields.phone.trim() || null,
  };
}

/** Only the fields an edit draft changed against the saved supplier. */
function changedFields(draft: ISupplierFields, of: Supplier): ISupplierPatch {
  const body = supplierBodyOf(draft);
  return {
    ...(body.name !== of.name ? { name: body.name } : {}),
    ...(body.contactName !== of.contactName ? { contactName: body.contactName } : {}),
    ...(body.phone !== of.phone ? { phone: body.phone } : {}),
  };
}

/**
 * The supplier keys of an order write. A new draft is `newSupplier` (and no
 * `supplierId` — the new supplier IS the order's). Otherwise the pick: a
 * create names a supplier only when one is picked; an edit sends the key only
 * when it changed, and `null` is how it is cleared. An edit draft adds
 * `supplierEdit` with the fields that changed, or nothing when none did.
 */
export function supplierPayloadOf(
  { id, draft }: IOrderSupplierChoice,
  initial: { supplierId: string | null } | null,
): Pick<DeliveryOrderBody, 'supplierId' | 'newSupplier' | 'supplierEdit'> {
  if (draft?.mode === 'new') return { newSupplier: supplierBodyOf(draft) };
  const pick = initial ? (id !== initial.supplierId ? { supplierId: id } : {}) : id != null ? { supplierId: id } : {};
  if (draft === null) return pick;
  const edit = changedFields(draft, draft.of);
  return Object.keys(edit).length > 0 ? { ...pick, supplierEdit: edit } : pick;
}
